import React from "react";
import {
    Badge,
    Flex,
    Icon,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
} from "@chakra-ui/react";
import { IoCheckmarkDoneCircleSharp } from "react-icons/io5";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import StatusBadge, {
    getStatusVariant,
} from "../../../../components/Badges/StatusBadge";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";

const AnalyticsTable = ({
    title,
    amount,
    captions,
    data = [],
    isLoading,
    variant,
}) => {
    const textColor = useColorModeValue(
        "gray.700",
        "white"
    );

    const formatCurrency = (value) =>
        `P${Number(value || 0).toLocaleString()}`;



    const renderRank = (index) => {
        if (index === 0) {
            return "🥇";
        }

        if (index === 1) {
            return "🥈";
        }

        if (index === 2) {
            return "🥉";
        }

        return `#${index + 1}`;
    };

    const renderRow = (row, index) => {
        switch (variant) {
            case "topPerformingAgents":
                return (
                    <>
                        <Td fontWeight="bold">
                            {renderRank(index)}
                        </Td>

                        <Td>
                            {row.agent_name ||
                                "Unknown"}
                        </Td>

                        <Td>
                            {row.agent_number ||
                                "N/A"}
                        </Td>

                        <Td>
                            {row.agent_packages ?? 0}
                        </Td>

                        <Td>
                            {formatCurrency(
                                row.revenue
                            )}
                        </Td>

                        <Td>
                            {formatCurrency(
                                row.commission
                            )}
                        </Td>

                        <Td>
                            {row.performance_score ??
                                0}
                        </Td>
                    </>
                );

            case "agentPerformance":
                return (
                    <>
                        <Td>
                            {row.agent_name ||
                                "Unknown"}
                        </Td>

                        <Td>
                            {row.agent_number ||
                                "N/A"}
                        </Td>

                    <Td>
    <StatusBadge
        label={row.status || "N/A"}
        variant={getStatusVariant(
            row.status
        )}
    />
</Td>

                        <Td>
                            {row.agent_packages ?? 0}
                        </Td>

                        <Td>
                            {formatCurrency(
                                row.revenue_generated
                            )}
                        </Td>

                        <Td>
                            {formatCurrency(
                                row.commission
                            )}
                        </Td>

                        <Td>
                            {row.performance_score ??
                                0}
                        </Td>
                    </>
                );

            case "programme":
            case "certification":
                return (
                    <>
                        <Td>
                            {row.metric}
                        </Td>

                        <Td>
                            {row.count ?? 0}
                        </Td>
                    </>
                );

            case "operations":
                return (
                    <>
                        <Td>
                            {row.metric}
                        </Td>

                        <Td>
                            {row.value ?? 0}
                        </Td>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <Card
            p="16px"
            overflowX={{
                sm: "scroll",
                xl: "hidden",
            }}
        >
            <CardHeader p="12px 0px 28px 0px">
                <Flex direction="column">
                    <Text
                        fontSize="lg"
                        color={textColor}
                        fontWeight="bold"
                        pb=".5rem"
                    >
                        {isLoading ? (
                            <Skeleton width={180} />
                        ) : (
                            title
                        )}
                    </Text>

                    <Flex align="center">
                        {isLoading ? (
                            <Skeleton
                                circle
                                width={16}
                                height={16}
                            />
                        ) : (
                            <Icon
                                as={
                                    IoCheckmarkDoneCircleSharp
                                }
                                color="teal.300"
                                w={4}
                                h={4}
                                pe="3px"
                            />
                        )}

                        <Text
                            ml={2}
                            color="gray.400"
                        >
                            {isLoading ? (
                                <Skeleton width={120} />
                            ) : (
                                <>
                                    <Text
                                        as="span"
                                        fontWeight="bold"
                                    >
                                        {amount}
                                    </Text>{" "}
                                    records
                                </>
                            )}
                        </Text>
                    </Flex>
                </Flex>
            </CardHeader>

            <Table
                variant="simple"
                color={textColor}
            >
                <Thead>
                    <Tr>
                        {captions.map(
                            (
                                caption,
                                index
                            ) => (
                                <Th
                                    key={index}
                                    color="gray.400"
                                >
                                    {isLoading ? (
                                        <Skeleton
                                            width={70}
                                        />
                                    ) : (
                                        caption
                                    )}
                                </Th>
                            )
                        )}
                    </Tr>
                </Thead>

                <Tbody>
                    {isLoading ? (
                        Array(5)
                            .fill(0)
                            .map(
                                (_, row) => (
                                    <Tr
                                        key={row}
                                    >
                                        {captions.map(
                                            (
                                                _,
                                                col
                                            ) => (
                                                <Td
                                                    key={
                                                        col
                                                    }
                                                >
                                                    <Skeleton />
                                                </Td>
                                            )
                                        )}
                                    </Tr>
                                )
                            )
                    ) : data.length > 0 ? (
                        data.map(
                            (
                                row,
                                index
                            ) => (
                                <Tr
                                    key={
                                        row.agent_id ||
                                        index
                                    }
                                >
                                    {renderRow(
                                        row,
                                        index
                                    )}
                                </Tr>
                            )
                        )
                    ) : (
                        <Tr>
                            <Td
                                colSpan={
                                    captions.length
                                }
                                textAlign="center"
                            >
                                <Text color="gray.500">
                                    No data available.
                                </Text>
                            </Td>
                        </Tr>
                    )}
                </Tbody>
            </Table>
        </Card>
    );
};

export default AnalyticsTable;